// Parent mode (B8, WI-17): while a grown-up is signed in on a child's phone, 30 minutes without a
// touch hands the phone back to the child (data/session `backToChild`). The clock pauses while a
// sheet or the plan is open (a grown-up reading isn't idle), warns in the last minute, and survives a
// reload: the last touch is stored (data/session `parentTouch`).
import { useEffect, useRef, useState } from 'react'
import { parentTouch, useSession } from '../data/session'
import { parentModeClock, type ParentModeClock } from '../domain/parentMode'

/** How often the banner's clock ticks. */
const TICK_MS = 1000

export interface ParentMode extends ParentModeClock {
  /** The child whose phone this is. */
  child: string
  paused: boolean
}

/** Parent mode's state for the banner, or null when this isn't parent mode. */
export function useParentMode(paused: boolean): ParentMode | null {
  const returnTo = useSession((s) => s.returnTo)
  const child = useSession((s) => s.people?.find((p) => p.id === s.returnTo)?.name ?? null)
  const grownUp = useSession((s) => !!s.person && s.person.kind !== 'child')
  const active = !!returnTo && grownUp
  const [now, setNow] = useState(() => Date.now())
  const pausedRef = useRef(paused)

  useEffect(() => {
    pausedRef.current = paused
    if (active && paused) parentTouch.write(Date.now())
  }, [active, paused])

  useEffect(() => {
    if (!active) return
    if (!parentTouch.read()) parentTouch.write(Date.now())
    const touch = () => parentTouch.write(Date.now())
    let over = false
    const tick = () => {
      if (over) return
      const at = Date.now()
      if (pausedRef.current) parentTouch.write(at)
      if (parentModeClock(parentTouch.read() ?? at, at).over) {
        over = true
        void useSession.getState().backToChild()
        return
      }
      setNow(at)
    }
    // A reload after the time ran out goes straight back to the child.
    tick()
    const timer = window.setInterval(tick, TICK_MS)
    window.addEventListener('pointerdown', touch, { passive: true })
    window.addEventListener('keydown', touch)
    return () => {
      clearInterval(timer)
      window.removeEventListener('pointerdown', touch)
      window.removeEventListener('keydown', touch)
    }
  }, [active])

  if (!active) return null
  return { ...parentModeClock(parentTouch.read() ?? now, now), child: child ?? '', paused }
}
